/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import type { Product } from '@shared/types/products';
import type { TemplateMetadata, CheckRepoInitializedResult, LocalChangesStatus } from '../../../../server/types';
import type { GitHubRepository, GitHubOrganization } from '../../../services/githubService';
import type { GitLabProject } from '../../../services/gitlabService';
import { GitLabRepoSelector } from '../GitLabRepoSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  ExternalLink,
  Github,
  GitBranch,
  Loader2,
  FolderOpen,
  Upload,
  Plus,
  Layers,
  HardDrive,
  GitMerge,
  Link2,
  RotateCcw,
  AlertTriangle,
} from 'lucide-react';

export type RepoConfigMode = 'none' | 'template' | 'link-remote';
export type SourceControlProviderType = 'github' | 'gitlab';

export interface SourceControlTabProps {
  product: Product;
  localPath: string | null;
  sourceControlProvider: SourceControlProviderType;
  hasProviderConfig: boolean;
  
  // Repo initialization state
  repoInitStatus: CheckRepoInitializedResult | null;
  templates: TemplateMetadata[];
  loadingTemplates: boolean;
  selectedTemplate: string;
  onTemplateSelect: (template: string) => void;
  isApplyingTemplate: boolean;
  onApplyTemplate: () => void;
  
  // Local repo push state
  showCreateRepoForm: boolean;
  onShowCreateRepoForm: (show: boolean) => void;
  newRepoName: string;
  onNewRepoNameChange: (name: string) => void;
  newRepoDescription: string;
  onNewRepoDescriptionChange: (desc: string) => void;
  newRepoIsPrivate: boolean;
  onNewRepoIsPrivateChange: (isPrivate: boolean) => void;
  isPushingToRemote: boolean;
  onPushToRemote: () => void;
  
  // Repo config mode state
  repoConfigMode: RepoConfigMode;
  onRepoConfigModeChange: (mode: RepoConfigMode) => void;
  availableRepos: (GitHubRepository | GitLabProject)[];
  loadingRepos: boolean;
  selectedRepoUrl: string;
  onSelectedRepoUrlChange: (url: string) => void;
  isLinkingRepo: boolean;
  onLinkToRemote: () => void;
  
  // Initialize from template
  isInitializingRepo: boolean;
  onInitializeFromTemplate: () => void;
  
  // Reset repository state
  isCheckingLocalChanges: boolean;
  localChangesStatus: LocalChangesStatus | null;
  isResettingRepo: boolean;
  onCheckLocalChanges: () => void;
  onResetRepo: (force: boolean) => void;
  
  // Organization selection (for pushing to remote)
  organizations?: GitHubOrganization[];
  loadingOrganizations?: boolean;
  selectedOrganization?: string;
  onSelectedOrganizationChange?: (org: string) => void;
}

export function SourceControlTab({
  product,
  localPath,
  sourceControlProvider,
  hasProviderConfig,
  repoInitStatus,
  templates,
  loadingTemplates,
  selectedTemplate,
  onTemplateSelect,
  isApplyingTemplate,
  onApplyTemplate,
  showCreateRepoForm,
  onShowCreateRepoForm,
  newRepoName,
  onNewRepoNameChange,
  newRepoDescription,
  onNewRepoDescriptionChange,
  newRepoIsPrivate,
  onNewRepoIsPrivateChange,
  isPushingToRemote,
  onPushToRemote,
  repoConfigMode,
  onRepoConfigModeChange,
  availableRepos,
  loadingRepos,
  selectedRepoUrl,
  onSelectedRepoUrlChange,
  isLinkingRepo,
  onLinkToRemote,
  isInitializingRepo,
  onInitializeFromTemplate,
  isCheckingLocalChanges,
  localChangesStatus,
  isResettingRepo,
  onCheckLocalChanges,
  onResetRepo,
  organizations = [],
  loadingOrganizations = false,
  selectedOrganization = '',
  onSelectedOrganizationChange,
}: SourceControlTabProps) {
  const hasRepo = !!product.repositoryUrl;
  const isLocalOnlyRepo = product.repositoryUrl?.startsWith('local/');
  const canResetRepo = hasRepo && !isLocalOnlyRepo; // Can only reset if there's a remote repo
  const providerIcon = sourceControlProvider === 'gitlab' 
    ? <GitBranch className="h-5 w-5" />
    : <Github className="h-5 w-5" />;
  const providerName = sourceControlProvider === 'gitlab' ? 'GitLab' : 'GitHub';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            {providerIcon}
            <CardTitle>Source Control</CardTitle>
          </div>
          <CardDescription>
            Repository configuration and version control settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {hasRepo ? (
            <>
              {/* Repository URL */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  {isLocalOnlyRepo ? (
                    <FolderOpen className="h-4 w-4" />
                  ) : (
                    <Github className="h-4 w-4" />
                  )}
                  {isLocalOnlyRepo ? 'Local Repository' : `${providerName} Repository`}
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-muted rounded-md text-sm font-mono break-all">
                    {product.repositoryUrl}
                  </code>
                  {!isLocalOnlyRepo && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const url = product.repositoryUrl?.startsWith('http') 
                          ? product.repositoryUrl 
                          : `https://github.com/${product.repositoryUrl}`;
                        window.open(url, '_blank');
                      }}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {isLocalOnlyRepo && (
                  <p className="text-xs text-muted-foreground">
                    This is a local-only repository. Push it to {providerName} to enable remote collaboration.
                  </p>
                )}
              </div>

              {/* Repo Path */}
              {product.repoPath && (
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <FolderOpen className="h-4 w-4" />
                    Repository Path
                  </label>
                  <code className="block p-2 bg-muted rounded-md text-sm font-mono">
                    {product.repoPath}
                  </code>
                </div>
              )}

              {/* Local Clone Path */}
              {localPath && (
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <FolderOpen className="h-4 w-4" />
                    Local Clone
                  </label>
                  <code className="block p-2 bg-muted rounded-md text-sm font-mono">
                    {localPath}
                  </code>
                </div>
              )}
              
              {/* Reset Repository - Only for remote repos */}
              {canResetRepo && (
                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-muted-foreground">
                    Reset local repo if corrupted or deleted
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onCheckLocalChanges}
                        disabled={isCheckingLocalChanges || isResettingRepo}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {isCheckingLocalChanges ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            Checking...
                          </>
                        ) : (
                          <>
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Reset
                          </>
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                          {localChangesStatus?.hasChanges && (
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                          )}
                          Reset Local Repository
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <div className="space-y-3">
                            {localChangesStatus?.hasChanges ? (
                              <>
                                <p className="text-destructive font-medium">
                                  Warning: You have local changes that will be lost!
                                </p>
                                <div className="bg-muted p-3 rounded-md text-sm space-y-2">
                                  {localChangesStatus.hasUncommittedChanges && (
                                    <div>
                                      <span className="font-medium">Uncommitted changes:</span>
                                      <ul className="list-disc list-inside ml-2 mt-1 text-muted-foreground">
                                        {localChangesStatus.uncommittedFiles.slice(0, 5).map((file, i) => (
                                          <li key={i} className="truncate">{file}</li>
                                        ))}
                                        {localChangesStatus.uncommittedFiles.length > 5 && (
                                          <li>...and {localChangesStatus.uncommittedFiles.length - 5} more</li>
                                        )}
                                      </ul>
                                    </div>
                                  )}
                                  {localChangesStatus.aheadOfMain > 0 && (
                                    <p>
                                      <span className="font-medium">{localChangesStatus.aheadOfMain}</span> commit(s) ahead of main
                                    </p>
                                  )}
                                  {localChangesStatus.unpushedCommits > 0 && (
                                    <p>
                                      <span className="font-medium">{localChangesStatus.unpushedCommits}</span> unpushed commit(s)
                                    </p>
                                  )}
                                </div>
                                <p>
                                  Are you sure you want to reset? This action cannot be undone.
                                </p>
                              </>
                            ) : (
                              <p>
                                This will delete the local repository and re-clone it from {providerName}.
                                Any local-only data will be lost.
                              </p>
                            )}
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel disabled={isResettingRepo}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onResetRepo(true)}
                          disabled={isResettingRepo}
                          className={localChangesStatus?.hasChanges ? 'bg-destructive hover:bg-destructive/90' : ''}
                        >
                          {isResettingRepo ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Resetting...
                            </>
                          ) : (
                            <>
                              <RotateCcw className="h-4 w-4 mr-2" />
                              {localChangesStatus?.hasChanges ? 'Reset Anyway' : 'Reset Repository'}
                            </>
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}

              {/* Initialize from Template Section - For blank/uninitialized repos */}
              {repoInitStatus && !repoInitStatus.initialized && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <div className="text-center py-4 bg-muted/30 rounded-lg border border-dashed">
                      <Layers className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                      <h3 className="text-sm font-medium mb-1">Repository Not Initialized</h3>
                      <p className="text-xs text-muted-foreground mb-4">
                        This repository is empty. Apply an architecture template to add project scaffolding.
                      </p>
                      
                      {loadingTemplates ? (
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading templates...
                        </div>
                      ) : templates.length > 0 ? (
                        <div className="space-y-3 px-4">
                          <select
                            value={selectedTemplate}
                            onChange={(e) => onTemplateSelect(e.target.value)}
                            className="w-full p-2 border rounded-md text-sm bg-background"
                            disabled={isApplyingTemplate}
                          >
                            <option value="">Choose a template...</option>
                            {templates.map((template) => (
                              <option key={template.name} value={template.name}>
                                {template.displayName} - {template.description}
                              </option>
                            ))}
                          </select>

                          {selectedTemplate && (
                            <div className="p-3 bg-background rounded-md text-left">
                              {(() => {
                                const template = templates.find(t => t.name === selectedTemplate);
                                return template ? (
                                  <div className="text-sm">
                                    <p className="font-medium">{template.displayName}</p>
                                    <p className="text-muted-foreground mt-1">{template.description}</p>
                                    <div className="flex gap-2 mt-2 flex-wrap">
                                      {template.features.frontend && <Badge variant="secondary">Frontend</Badge>}
                                      {template.features.backend && <Badge variant="secondary">Backend</Badge>}
                                      {template.features.database && <Badge variant="secondary">Database</Badge>}
                                    </div>
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          )}

                          <Button
                            onClick={onApplyTemplate}
                            disabled={!selectedTemplate || isApplyingTemplate}
                            className="w-full gap-2"
                          >
                            {isApplyingTemplate ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Applying Template...
                              </>
                            ) : (
                              <>
                                <Layers className="h-4 w-4" />
                                Apply Template
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No templates available.
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
              
              {/* Push to Remote Section - Only for local repos */}
              {isLocalOnlyRepo && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium">Push to {providerName}</h3>
                        <p className="text-xs text-muted-foreground">
                          Create a remote repository and push your local code
                        </p>
                      </div>
                      {!showCreateRepoForm && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            onShowCreateRepoForm(true);
                            // Pre-fill with product name
                            onNewRepoNameChange(product.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
                            onNewRepoDescriptionChange(product.description || '');
                          }}
                          disabled={!hasProviderConfig}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Push to Remote
                        </Button>
                      )}
                    </div>
                    
                    {!hasProviderConfig && (
                      <div className="p-3 bg-muted/50 rounded-md border border-dashed">
                        <p className="text-xs text-muted-foreground">
                          Configure your {providerName} access token in Settings to enable pushing to remote.
                        </p>
                      </div>
                    )}
                    
                    {showCreateRepoForm && hasProviderConfig && (
                      <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Repository Name</label>
                          <Input
                            value={newRepoName}
                            onChange={(e) => onNewRepoNameChange(e.target.value)}
                            placeholder="my-awesome-project"
                            disabled={isPushingToRemote}
                          />
                        </div>
                        
                        {/* Organization Selector (GitHub only) */}
                        {sourceControlProvider === 'github' && (
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Owner</label>
                            {loadingOrganizations ? (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading organizations...
                              </div>
                            ) : (
                              <select
                                value={selectedOrganization}
                                onChange={(e) => onSelectedOrganizationChange?.(e.target.value)}
                                className="w-full p-2 border rounded-md text-sm bg-background"
                                disabled={isPushingToRemote}
                              >
                                <option value="">Personal Account</option>
                                {organizations.map((org) => (
                                  <option key={org.login} value={org.login}>
                                    {org.login}
                                    {org.description ? ` - ${org.description.substring(0, 30)}${org.description.length > 30 ? '...' : ''}` : ''}
                                  </option>
                                ))}
                              </select>
                            )}
                            <p className="text-xs text-muted-foreground">
                              Select where to create the repository
                            </p>
                          </div>
                        )}
                        
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Description (optional)</label>
                          <Textarea
                            value={newRepoDescription}
                            onChange={(e) => onNewRepoDescriptionChange(e.target.value)}
                            placeholder="A brief description of your project"
                            rows={2}
                            disabled={isPushingToRemote}
                          />
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="private-repo"
                            checked={newRepoIsPrivate}
                            onChange={(e) => onNewRepoIsPrivateChange(e.target.checked)}
                            disabled={isPushingToRemote}
                            className="rounded"
                          />
                          <label htmlFor="private-repo" className="text-sm">
                            Private repository
                          </label>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={onPushToRemote}
                            disabled={isPushingToRemote || !newRepoName.trim()}
                            className="gap-2"
                          >
                            {isPushingToRemote ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Creating & Pushing...
                              </>
                            ) : (
                              <>
                                <Plus className="h-4 w-4" />
                                Create & Push
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              onShowCreateRepoForm(false);
                              onNewRepoNameChange('');
                              onNewRepoDescriptionChange('');
                              onSelectedOrganizationChange?.('');
                            }}
                            disabled={isPushingToRemote}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
              
            </>
          ) : (
            <div className="space-y-6">
              {/* Header */}
              <div className="text-center py-4">
                <HardDrive className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Repository Not Configured</h3>
                <p className="text-sm text-muted-foreground">
                  Initialize a local repository from a template or link to an existing {providerName} repository.
                </p>
              </div>

              <Separator />

              {/* Mode Selection */}
              {repoConfigMode === 'none' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Initialize from Template */}
                  <Card 
                    className="cursor-pointer hover:border-primary transition-colors"
                    onClick={() => onRepoConfigModeChange('template')}
                  >
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <Layers className="h-10 w-10 mx-auto text-primary mb-3" />
                        <h4 className="font-medium mb-2">Initialize from Template</h4>
                        <p className="text-xs text-muted-foreground">
                          Create a new repository with pre-configured project scaffolding
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Link to Remote Repository */}
                  <Card 
                    className={`cursor-pointer transition-colors ${hasProviderConfig ? 'hover:border-primary' : 'opacity-60'}`}
                    onClick={() => hasProviderConfig && onRepoConfigModeChange('link-remote')}
                  >
                    <CardContent className="pt-6">
                      <div className="text-center">
                        {sourceControlProvider === 'github' ? (
                          <Github className="h-10 w-10 mx-auto text-primary mb-3" />
                        ) : (
                          <GitMerge className="h-10 w-10 mx-auto text-primary mb-3" />
                        )}
                        <h4 className="font-medium mb-2">Link to {providerName}</h4>
                        <p className="text-xs text-muted-foreground">
                          {hasProviderConfig 
                            ? `Connect to an existing ${providerName} repository`
                            : `Configure ${providerName} access token in Settings first`
                          }
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Initialize from Template Form */}
              {repoConfigMode === 'template' && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Initialize from Template
                    </h4>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        onRepoConfigModeChange('none');
                        onTemplateSelect('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>

                  {loadingTemplates ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading templates...
                    </div>
                  ) : templates.length > 0 ? (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Select Template</label>
                        <select
                          value={selectedTemplate}
                          onChange={(e) => onTemplateSelect(e.target.value)}
                          className="w-full p-2 border rounded-md text-sm bg-background"
                          disabled={isInitializingRepo}
                        >
                          <option value="">Choose a template...</option>
                          {templates.map((template) => (
                            <option key={template.name} value={template.name}>
                              {template.displayName} - {template.description}
                            </option>
                          ))}
                        </select>
                      </div>

                      {selectedTemplate && (
                        <div className="p-3 bg-muted rounded-md">
                          {(() => {
                            const template = templates.find(t => t.name === selectedTemplate);
                            return template ? (
                              <div className="text-sm">
                                <p className="font-medium">{template.displayName}</p>
                                <p className="text-muted-foreground mt-1">{template.description}</p>
                                <div className="flex gap-2 mt-2 flex-wrap">
                                  {template.features.frontend && <Badge variant="secondary">Frontend</Badge>}
                                  {template.features.backend && <Badge variant="secondary">Backend</Badge>}
                                  {template.features.database && <Badge variant="secondary">Database</Badge>}
                                </div>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      )}

                      <Button
                        onClick={onInitializeFromTemplate}
                        disabled={!selectedTemplate || isInitializingRepo}
                        className="w-full gap-2"
                      >
                        {isInitializingRepo ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Initializing...
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4" />
                            Initialize Repository
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground text-center py-4">
                      No templates available. Templates may not be configured.
                    </div>
                  )}
                </div>
              )}

              {/* Link to Remote Repository Form */}
              {repoConfigMode === 'link-remote' && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                      {sourceControlProvider === 'github' ? (
                        <Github className="h-4 w-4" />
                      ) : (
                        <GitMerge className="h-4 w-4" />
                      )}
                      Link to {providerName} Repository
                    </h4>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        onRepoConfigModeChange('none');
                        onSelectedRepoUrlChange('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>

                  {loadingRepos ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading repositories...
                    </div>
                  ) : availableRepos.length > 0 ? (
                    <>
                      {sourceControlProvider === 'gitlab' ? (
                        <GitLabRepoSelector
                          projects={availableRepos as GitLabProject[]}
                          selectedRepoUrl={selectedRepoUrl}
                          onSelectedRepoUrlChange={onSelectedRepoUrlChange}
                          disabled={isLinkingRepo}
                        />
                      ) : (
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Select Repository</label>
                          <select
                            value={selectedRepoUrl}
                            onChange={(e) => onSelectedRepoUrlChange(e.target.value)}
                            className="w-full p-2 border rounded-md text-sm bg-background"
                            disabled={isLinkingRepo}
                          >
                            <option value="">Choose a repository...</option>
                            {(availableRepos as GitHubRepository[]).map((repo) => (
                              <option key={repo.full_name} value={repo.html_url}>
                                {repo.full_name} ({repo.private ? 'Private' : 'Public'})
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <p className="text-xs text-muted-foreground">
                        The selected repository will be cloned locally and linked to this product.
                      </p>

                      <Button
                        onClick={onLinkToRemote}
                        disabled={!selectedRepoUrl || isLinkingRepo}
                        className="w-full gap-2"
                      >
                        {isLinkingRepo ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Cloning & Linking...
                          </>
                        ) : (
                          <>
                            <Link2 className="h-4 w-4" />
                            Link Repository
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground text-center py-4">
                      No repositories found. Make sure your {providerName} token has access to at least one repository.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
